import { isNotNil } from "es-toolkit";
import { Html } from "@react-three/drei";
import { MUSCLES, type MuscleId } from "@/entities/muscle/model/muscles";
import { cn } from "@/shared/lib/utils";
import type { Highlight } from "@/entities/muscle/ui/limb-rigging.utils";

type MuscleLabelProps = Readonly<{
  muscleId: MuscleId;
  text: string;
  center: readonly [number, number, number];
  highlight: Highlight | undefined;
}>;

const resolveBorderColor = (highlight: Highlight | undefined): string => {
  if (highlight === "load-on") return "border-emerald-500";

  if (highlight === "load-over") return "border-amber-500";

  return "border-destructive";
};

const resolveValueColor = (highlight: Highlight | undefined): string => {
  if (highlight === "load-on") return "text-emerald-500";

  if (highlight === "load-over") return "text-amber-500";

  return "text-destructive";
};

export const MuscleLabel = ({ muscleId, text, center, highlight }: MuscleLabelProps) => (
  <Html key={`label-${muscleId}`} position={center} center distanceFactor={6} zIndexRange={[10, 0]}>
    <div
      data-testid={`muscle-label${isNotNil(highlight) ? "--active" : ""}`}
      data-muscle-id={muscleId}
      className={cn(
        "pointer-events-auto inline-flex flex-col items-center gap-0.5 whitespace-nowrap rounded-[var(--r-sm)] border-[1.5px] bg-background/85 px-2 py-1 font-mono text-2xs uppercase tracking-[0.06em] leading-tight text-foreground backdrop-blur-md [transform:translateZ(0)]",
        resolveBorderColor(highlight),
      )}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <span className="font-semibold text-muted-foreground" data-testid="muscle-label__name">
        {MUSCLES[muscleId].label}
      </span>
      <span
        className={cn("font-bold tabular-nums", resolveValueColor(highlight))}
        data-testid="muscle-label__value"
      >
        {text}
      </span>
    </div>
  </Html>
);
